import { useDatoPerson } from '@src/stores-v2/StoreContext';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * On the home page, when the URL has no `showcase` or `storyId`, redirect to the
 * same page with `?env=<env>&showcase=<firstOrg.slug>` once the person's
 * organizations have loaded. This opens the first available organization by
 * feeding the `showcase` slug to `useVideoCreatorDataInitialization`, which
 * loads it into `videoCreator.organization`.
 *
 * No-op when a `showcase` or `storyId` param is already present (both can
 * initialize data, so we never override an explicit deep-link, and it never
 * loops) or when the person has no organizations. The caller must be a MobX
 * `observer` so the effect re-runs when `DatoPersonStore.organizations`
 * populates asynchronously.
 */
export function useDefaultOrgRedirect() {
  const datoPerson = useDatoPerson();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hasInitParam =
    !!searchParams.get('showcase') || !!searchParams.get('storyId');
  const firstOrgSlug = datoPerson.getFallbackOrganization()?.slug;

  useEffect(() => {
    if (hasInitParam || !firstOrgSlug) return;

    const params = new URLSearchParams(searchParams);
    params.set(
      'env',
      searchParams.get('env') || process.env.REACT_APP_DATO_ENV || '',
    );
    params.set('showcase', firstOrgSlug);
    navigate(`/home?${params.toString()}`, { replace: true });
  }, [hasInitParam, firstOrgSlug, searchParams, navigate]);
}
